import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Role } from "./types/permissions";
import { CRUD_VERBS, ModelGroup, VERB_LABEL, humanize } from "./permissionUtils";

interface Stats {
  appsSelected: number;
  modelsSelected: number;
  permissionsSelected: number;
  customSelected: number;
}

interface PermissionReviewScreenProps {
  role: Role;
  groupedByApp: Record<string, ModelGroup[]>;
  granted: Set<number>;
  stats: Stats;
  isSaving: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const PermissionReviewScreen: React.FC<PermissionReviewScreenProps> = ({
  role,
  groupedByApp,
  granted,
  stats,
  isSaving,
  onBack,
  onConfirm,
}) => {
  const selectedApps = Object.entries(groupedByApp)
    .map(([app, models]) => ({
      app,
      models: models.filter((m) => m.all.some((p) => granted.has(p.id))),
    }))
    .filter((entry) => entry.models.length > 0);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBack}
        sx={{ mb: 2 }}
      >
        Back to permissions
      </Button>

      <Paper sx={{ boxShadow: 0, p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Review role configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Confirm the permissions assigned to <strong>{role.name}</strong>{" "}
          before saving.
        </Typography>

        {role.is_managed && (
          <Chip
            label="Managed role — changes reset on next deploy"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ mt: 1.5 }}
          />
        )}

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr 1fr", sm: "repeat(4, 1fr)" }}
          gap={2}
          mt={3}
        >
          <StatTile label="Applications" value={stats.appsSelected} />
          <StatTile label="Models" value={stats.modelsSelected} />
          <StatTile label="Permissions" value={stats.permissionsSelected} />
          <StatTile label="Custom" value={stats.customSelected} />
        </Box>
      </Paper>

      <Paper sx={{ boxShadow: 0, p: 3, mb: 3, maxHeight: "50vh", overflow: "auto" }}>
        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
          Applications &amp; Permissions
        </Typography>
        {selectedApps.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No permissions assigned to this role.
          </Typography>
        ) : (
          selectedApps.map(({ app, models }) => (
            <Box key={app} mb={2.5}>
              <Typography variant="body2" fontWeight="bold">
                {humanize(app)}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({models.length} model{models.length !== 1 ? "s" : ""})
                </Typography>
              </Typography>
              <Box pl={2} mt={1}>
                {models.map((m) => (
                  <Box
                    key={m.model}
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.75}
                    mb={0.75}
                  >
                    <Typography variant="caption" sx={{ width: 140 }}>
                      {humanize(m.model)}
                    </Typography>
                    {CRUD_VERBS.filter((v) => m.crud[v] && granted.has(m.crud[v]!.id)).map(
                      (v) => (
                        <Chip
                          key={v}
                          label={VERB_LABEL[v]}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ),
                    )}
                    {m.other
                      .filter((p) => granted.has(p.id))
                      .map((p) => (
                        <Chip key={p.id} label={p.name} size="small" color="info" variant="outlined" />
                      ))}
                  </Box>
                ))}
              </Box>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))
        )}
      </Paper>

      <Box display="flex" justifyContent="space-between">
        <Button onClick={onBack} variant="outlined">
          Back
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} /> : <CheckCircleIcon />}
        >
          {isSaving ? "Saving..." : "Confirm & Save Role"}
        </Button>
      </Box>
    </Box>
  );
};

const StatTile: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
    <Typography variant="caption" color="text.secondary" textTransform="uppercase">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold">
      {value}
    </Typography>
  </Paper>
);

export default PermissionReviewScreen;
